import * as types from "./actionTypes";
import * as sessionsService from "../../../Shared/Auto/ApiDefinitions/MentorSessionsService";
import * as queueService from "../../../Shared/Auto/ApiDefinitions/MentorQueuesService";
import * as userService from "../../../Shared/Auto/ApiDefinitions/MentorUserService";
import { beginApiCall, apiCallError } from "./apiStatusActions";
import { AppThunk } from "./AppThunk";
import {
    MentorSession,
    MentorUserInfo,
    CompletedSessionStub,
    SessionStub,
    ActiveSessionStub,
} from "../../../Shared/GeneratedTypes";
import { apiErrorOccurred } from "../../../Shared/Redux/Actions/sharedActions";

export function loadCurrentMentorById(id: string): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return sessionsService
            .getSessionById(id, mentorAccess.token)
            .then((session) => {
                dispatch(loadCurrentMentorByIdSuccess(session));
            })
            .catch((error) => {
                dispatch(apiCallError());
                dispatch(apiErrorOccurred(error, "Failed to load GuideStream"));
            });
    };
}

function loadCurrentMentorByIdSuccess(session: MentorSession) {
    return {
        type: types.LOAD_CURRENT_MENTORS_BY_ID_SUCCESS as typeof types.LOAD_CURRENT_MENTORS_BY_ID_SUCCESS,
        session,
    };
}

export function getUserInfo(id: string | null): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());

        return userService
            .userInfo(id ?? "", mentorAccess.token)
            .then((result) => {
                dispatch(getUserInfoSuccess(result));
            })
            .catch((error) => {
                dispatch(apiCallError());
                dispatch(apiErrorOccurred(error, "Failed to load GuideStream"));
            });
    };
}

function getUserInfoSuccess(userInfo: MentorUserInfo) {
    return {
        type: types.GET_MENTOR_USER_INFO_SUCCESS as typeof types.GET_MENTOR_USER_INFO_SUCCESS,
        userInfo,
    };
}

export function loadAssignedMentors(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .assignedQueue(mentorAccess.token)
            .then((assignedMentors) => {
                dispatch(loadAssignedMentorsSuccess(assignedMentors));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function loadAssignedMentorsSuccess(assignedMentors: SessionStub[]) {
    return {
        type: types.LOAD_ASSIGNED_MENTORS_SUCCESS as typeof types.LOAD_ASSIGNED_MENTORS_SUCCESS,
        assignedMentors,
    };
}

export function loadUpcomingMentors(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .upcomingSessions(mentorAccess.token)
            .then((upcomingMentors) => {
                dispatch(loadUpcomingMentorsSuccess(upcomingMentors));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function loadUpcomingMentorsSuccess(upcomingMentors: SessionStub[]) {
    return {
        type: types.LOAD_UPCOMING_MENTORS_SUCCESS as typeof types.LOAD_UPCOMING_MENTORS_SUCCESS,
        upcomingMentors,
    };
}

export function loadCompletedMentors(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .recentlyCompleted(mentorAccess.token)
            .then((completedMentors) => {
                dispatch(loadCompletedMentorsSuccess(completedMentors));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function loadCompletedMentorsSuccess(completedMentors: CompletedSessionStub[]) {
    return {
        type: types.LOAD_COMPLETED_MENTORS_SUCCESS as typeof types.LOAD_COMPLETED_MENTORS_SUCCESS,
        completedMentors,
    };
}

export function loadQueuedMentors(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .queuedSessions(mentorAccess.token)
            .then((queuedSessions: SessionStub[]) => {
                dispatch(loadQueuedMentorsSuccess(queuedSessions));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function loadQueuedMentorsSuccess(queuedSessions: SessionStub[]) {
    return {
        type: types.LOAD_QUEUED_MENTORS_SUCCESS as typeof types.LOAD_QUEUED_MENTORS_SUCCESS,
        queuedSessions,
    };
}

export function loadPartiallyComplete(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .partiallyComplete(mentorAccess.token)
            .then((partiallyCompleteSessions: SessionStub[]) => {
                dispatch(loadPartiallyCompleteSessionsSuccess(partiallyCompleteSessions));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function loadPartiallyCompleteSessionsSuccess(partiallyCompleteSessions: SessionStub[]) {
    return {
        type: types.LOAD_PARTIALLY_COMPLETE_SESSIONS_SUCCESS as typeof types.LOAD_PARTIALLY_COMPLETE_SESSIONS_SUCCESS,
        partiallyCompleteSessions,
    };
}

export function loadActiveMentors(): AppThunk {
    return function (dispatch, getState) {
        const { mentorAccess } = getState();
        dispatch(beginApiCall());
        return queueService
            .activeSessions(mentorAccess.token)
            .then((activeSessions) => {
                dispatch(loadActiveMentorsSuccess(activeSessions));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function loadActiveMentorsSuccess(activeMentors: ActiveSessionStub[]) {
    return {
        type: types.LOAD_ACTIVE_MENTORS_SUCCESS as typeof types.LOAD_ACTIVE_MENTORS_SUCCESS,
        activeMentors,
    };
}

export function beginSession(customerId: string, sessionId: string): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginSessionSuccess(customerId, sessionId));
    };
}
function beginSessionSuccess(customerId: string, sessionId: string) {
    return {
        type: types.BEGIN_SESSION_SUCCESS as typeof types.BEGIN_SESSION_SUCCESS,
        sessionId,
        customerId,
    };
}

export type SessionActions =
    | ReturnType<typeof loadCurrentMentorByIdSuccess>
    | ReturnType<typeof loadCompletedMentorsSuccess>
    | ReturnType<typeof loadQueuedMentorsSuccess>
    | ReturnType<typeof loadActiveMentorsSuccess>
    | ReturnType<typeof loadAssignedMentorsSuccess>
    | ReturnType<typeof loadUpcomingMentorsSuccess>
    | ReturnType<typeof loadPartiallyCompleteSessionsSuccess>
    | ReturnType<typeof beginSessionSuccess>;
